import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { EntityContext } from '../entity';
import PdfComp from '../components/pdf-reader';

const createEmptyVariable = () => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  key: '',
  type: 'string',
  value: '',
  table: {
    headers: [''],
    rows: [],
  },
});

const normalizeTableValue = (table) => {
  const headers = (table?.headers || []).map((header) => `${header || ''}`.trim());
  const rowSize = headers.length;

  const rows = (table?.rows || []).map((row) => {
    const nextRow = [...row];
    while (nextRow.length < rowSize) nextRow.push('');
    return nextRow.slice(0, rowSize).map((cell) => `${cell || ''}`);
  });

  return { headers, rows };
};

export default function TemplateGenerateDocumentPage() {
  const { templateKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getTemplate, createDocumentByTemplate, loading } = useContext(EntityContext);
  const getTemplateRef = useRef(getTemplate);

  const [templateName, setTemplateName] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [variables, setVariables] = useState([createEmptyVariable()]);
  const [pageError, setPageError] = useState('');
  const [pageSuccess, setPageSuccess] = useState('');
  const [generatedDocumentKey, setGeneratedDocumentKey] = useState('');
  const [generatedDocumentUrl, setGeneratedDocumentUrl] = useState('');
  const [showGeneratedPreview, setShowGeneratedPreview] = useState(false);
  const [generatedTemplatePayload, setGeneratedTemplatePayload] = useState(null);
  const hasInitializedFromNavigationState = useRef(false);

  useEffect(() => {
    getTemplateRef.current = getTemplate;
  }, [getTemplate]);

  useEffect(() => {
    if (!templateKey) return;
    getTemplateRef.current(templateKey)
      .then((data) => {
        if (data && !data.error) {
          setTemplateName(data.template_name || '');
        }
      })
      .catch(() => {});
  }, [templateKey]);

  useEffect(() => {
    if (hasInitializedFromNavigationState.current) return;
    hasInitializedFromNavigationState.current = true;

    const restoredState = location.state?.templateGeneratorState;
    if (!restoredState) return;

    setTemplateName(restoredState.templateName || '');
    setDocumentName(restoredState.documentName || '');
    setVariables(
      Array.isArray(restoredState.variables) && restoredState.variables.length > 0
        ? restoredState.variables
        : [createEmptyVariable()]
    );
    setGeneratedDocumentKey(restoredState.generatedDocumentKey || '');
    setGeneratedDocumentUrl(restoredState.generatedDocumentUrl || '');
    setShowGeneratedPreview(Boolean(restoredState.showGeneratedPreview));
    setGeneratedTemplatePayload(restoredState.generatedTemplatePayload || null);
    setPageSuccess('');
    setPageError('');
  }, [location.state]);

  const handleAddVariable = useCallback(() => {
    setVariables((prev) => [...prev, createEmptyVariable()]);
  }, []);

  const handleRemoveVariable = useCallback((variableId) => {
    setVariables((prev) => prev.filter((variable) => variable.id !== variableId));
  }, []);

  const handleVariableChange = useCallback((variableId, patch) => {
    setVariables((prev) =>
      prev.map((variable) => (variable.id === variableId ? { ...variable, ...patch } : variable))
    );
  }, []);

  const handleVariableTypeChange = useCallback((variableId, type) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        return {
          ...variable,
          type,
          value: type === 'string' ? variable.value || '' : '',
          table: type === 'table' ? variable.table || { headers: [''], rows: [] } : variable.table,
        };
      })
    );
  }, []);

  const handleAddHeader = useCallback((variableId) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        const nextHeaders = [...variable.table.headers, ''];
        const nextRows = variable.table.rows.map((row) => [...row, '']);
        return { ...variable, table: { headers: nextHeaders, rows: nextRows } };
      })
    );
  }, []);

  const handleRemoveHeader = useCallback((variableId, headerIndex) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        if (variable.table.headers.length <= 1) return variable;
        const nextHeaders = variable.table.headers.filter((_, index) => index !== headerIndex);
        const nextRows = variable.table.rows.map((row) => row.filter((_, index) => index !== headerIndex));
        return { ...variable, table: { headers: nextHeaders, rows: nextRows } };
      })
    );
  }, []);

  const handleHeaderChange = useCallback((variableId, headerIndex, value) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        const nextHeaders = [...variable.table.headers];
        nextHeaders[headerIndex] = value;
        return { ...variable, table: { ...variable.table, headers: nextHeaders } };
      })
    );
  }, []);

  const handleAddRow = useCallback((variableId) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        const newRow = new Array(variable.table.headers.length).fill('');
        return { ...variable, table: { ...variable.table, rows: [...variable.table.rows, newRow] } };
      })
    );
  }, []);

  const handleRemoveRow = useCallback((variableId, rowIndex) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        const nextRows = variable.table.rows.filter((_, index) => index !== rowIndex);
        return { ...variable, table: { ...variable.table, rows: nextRows } };
      })
    );
  }, []);

  const handleCellChange = useCallback((variableId, rowIndex, cellIndex, value) => {
    setVariables((prev) =>
      prev.map((variable) => {
        if (variable.id !== variableId) return variable;
        const nextRows = variable.table.rows.map((row, currentRowIndex) => {
          if (currentRowIndex !== rowIndex) return row;
          const nextRow = [...row];
          nextRow[cellIndex] = value;
          return nextRow;
        });
        return { ...variable, table: { ...variable.table, rows: nextRows } };
      })
    );
  }, []);

  const hasAtLeastOneVariable = useMemo(
    () => variables.some((variable) => variable.key.trim() !== ''),
    [variables]
  );

  const validatePayload = () => {
    if (!documentName.trim()) return 'Informe o nome do documento.';
    if (!templateKey) return 'Template inválido.';
    if (!hasAtLeastOneVariable) return 'Adicione ao menos uma variável com nome.';

    const invalidVariable = variables.find((variable) => {
      const variableKey = variable.key.trim();
      if (!variableKey) return true;

      if (variable.type === 'string') {
        return !`${variable.value || ''}`.trim();
      }

      const normalizedTable = normalizeTableValue(variable.table);
      if (normalizedTable.headers.length === 0) return true;
      if (normalizedTable.headers.some((header) => !header)) return true;
      return false;
    });

    if (invalidVariable) {
      const variableKey = invalidVariable.key.trim();
      if (!variableKey) return 'Toda variável precisa ter nome.';

      if (invalidVariable.type === 'string') {
        return `Preencha o valor da variável "${variableKey}".`;
      }

      const normalizedTable = normalizeTableValue(invalidVariable.table);
      if (normalizedTable.headers.length === 0) {
        return `A tabela da variável "${variableKey}" precisa de ao menos uma coluna.`;
      }
      return `Preencha todos os nomes de coluna na variável "${variableKey}".`;
    }

    return '';
  };

  const handleGenerate = async () => {
    setPageError('');
    setPageSuccess('');
    setGeneratedDocumentKey('');
    setGeneratedDocumentUrl('');

    const validationError = validatePayload();
    if (validationError) {
      setPageError(validationError);
      return;
    }

    const payloadVariables = variables.map((variable) => {
      if (variable.type === 'string') {
        return {
          key: variable.key.trim(),
          value: `${variable.value || ''}`,
          type: 'string',
        };
      }

      return {
        key: variable.key.trim(),
        value: normalizeTableValue(variable.table),
        type: 'table',
      };
    });

    const templatePayload = {
      document_name: documentName.trim(),
      template_key: templateKey,
      variables: payloadVariables,
    };

    try {
      const response = await createDocumentByTemplate(
        templatePayload.document_name,
        templatePayload.template_key,
        templatePayload.variables
      );
      const documentKey = response?.document_key || response?.documentKey || '';
      const documentUrl = response?.url || '';
      setGeneratedDocumentKey(documentKey);
      setGeneratedDocumentUrl(documentUrl);
      setGeneratedTemplatePayload(templatePayload);
      setPageSuccess('Documento gerado com sucesso.');
      setShowGeneratedPreview(true);
    } catch (error) {
      setPageError(error?.response?.data?.detail || 'Erro ao gerar documento pelo template.');
    }
  };

  return (
    <>
      <Helmet>
        <title> Gerar Documento por Template </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Gerar Documento por Template</Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/templates')}
          >
            Voltar
          </Button>
        </Stack>

        <Card sx={{ p: 3 }}>
          <Stack spacing={2}>
            {!showGeneratedPreview ? (
              <>
                <TextField
                  label="Template selecionado"
                  value={templateName || templateKey || ''}
                  InputProps={{ readOnly: true }}
                />

                <TextField
                  label="Nome do documento"
                  value={documentName}
                  onChange={(event) => setDocumentName(event.target.value)}
                  placeholder="Ex.: Contrato Cliente XPTO"
                />

                <Divider />

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">Variáveis</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddVariable}>
                    Adicionar variável
                  </Button>
                </Stack>

                {variables.map((variable, variableIndex) => (
                  <Accordion key={variable.id} defaultExpanded={variableIndex === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>
                          {variable.key.trim() || `Variável ${variableIndex + 1}`}
                        </Typography>
                        <Chip size="small" label={variable.type} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Nome da variável (key)"
                            value={variable.key}
                            onChange={(event) =>
                              handleVariableChange(variable.id, { key: event.target.value })
                            }
                          />
                          <TextField
                            select
                            fullWidth
                            label="Tipo"
                            value={variable.type}
                            onChange={(event) => handleVariableTypeChange(variable.id, event.target.value)}
                          >
                            <MenuItem value="string">string</MenuItem>
                            <MenuItem value="table">table</MenuItem>
                          </TextField>
                          <Box display="flex" alignItems="center">
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveVariable(variable.id)}
                              disabled={variables.length <= 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Stack>

                        {variable.type === 'string' && (
                          <TextField
                            multiline
                            minRows={3}
                            label="Valor"
                            value={variable.value}
                            onChange={(event) =>
                              handleVariableChange(variable.id, { value: event.target.value })
                            }
                          />
                        )}

                        {variable.type === 'table' && (
                          <Stack spacing={2}>
                            <Typography variant="subtitle2">Colunas</Typography>
                            <Stack spacing={1}>
                              {variable.table.headers.map((header, headerIndex) => (
                                <Stack key={`${variable.id}-header-${headerIndex}`} direction="row" spacing={1}>
                                  <TextField
                                    fullWidth
                                    label={`Coluna ${headerIndex + 1}`}
                                    value={header}
                                    onChange={(event) =>
                                      handleHeaderChange(variable.id, headerIndex, event.target.value)
                                    }
                                  />
                                  <IconButton
                                    color="error"
                                    onClick={() => handleRemoveHeader(variable.id, headerIndex)}
                                    disabled={variable.table.headers.length <= 1}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Stack>
                              ))}
                            </Stack>
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddHeader(variable.id)}
                            >
                              Adicionar coluna
                            </Button>

                            <Divider />
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography variant="subtitle2">Linhas</Typography>
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddRow(variable.id)}
                              >
                                Adicionar linha
                              </Button>
                            </Stack>

                            <Stack spacing={1}>
                              {variable.table.rows.map((row, rowIndex) => (
                                <Card key={`${variable.id}-row-${rowIndex}`} variant="outlined" sx={{ p: 2 }}>
                                  <Stack spacing={1}>
                                    {row.map((cell, cellIndex) => (
                                      <TextField
                                        key={`${variable.id}-row-${rowIndex}-cell-${cellIndex}`}
                                        label={variable.table.headers[cellIndex] || `Coluna ${cellIndex + 1}`}
                                        value={cell}
                                        onChange={(event) =>
                                          handleCellChange(variable.id, rowIndex, cellIndex, event.target.value)
                                        }
                                      />
                                    ))}
                                    <Box>
                                      <Button
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleRemoveRow(variable.id, rowIndex)}
                                      >
                                        Remover linha
                                      </Button>
                                    </Box>
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            ) : (
              <>
                <Typography variant="h6">Documento gerado</Typography>
                {generatedDocumentUrl ? (
                  <Box sx={{ height: '70vh', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <PdfComp pdfFile={generatedDocumentUrl} />
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Documento gerado, mas a URL do PDF não foi retornada.
                  </Alert>
                )}
              </>
            )}

            {pageError ? <Alert severity="error">{pageError}</Alert> : null}
            {pageSuccess ? (
              <Alert severity="success">
                {pageSuccess}
                {generatedDocumentKey ? ` Chave do documento: ${generatedDocumentKey}` : ''}
              </Alert>
            ) : null}

            {!showGeneratedPreview ? (
              <Stack direction="row" justifyContent="flex-end" pt={1}>
                <Button variant="contained" onClick={handleGenerate} disabled={loading}>
                  {loading ? 'Gerando...' : 'Gerar documento'}
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between" pt={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowGeneratedPreview(false);
                    setPageSuccess('');
                    setPageError('');
                  }}
                >
                  Voltar para variáveis
                </Button>
                <Button
                  variant="contained"
                  disabled={!generatedTemplatePayload}
                  onClick={() =>
                    navigate(`/dashboard/templates/${templateKey}/configure-envelope`, {
                      state: {
                        templatePayload: generatedTemplatePayload,
                        generatedDocumentUrl,
                        generatedDocumentKey,
                        templateGeneratorState: {
                          templateName,
                          documentName,
                          variables,
                          generatedDocumentKey,
                          generatedDocumentUrl,
                          showGeneratedPreview: true,
                          generatedTemplatePayload,
                        },
                      },
                    })
                  }
                >
                  Seguir
                </Button>
              </Stack>
            )}
          </Stack>
        </Card>
      </Container>
    </>
  );
}
